import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import * as d3 from 'd3'
// import { textWrap } from 'd3plus-text'
import { useElementSize } from 'usehooks-ts'
import {
  getFreshNodes,
  initializeSimulation,
  updateSimulation,
  setSvgPositions,
} from './Utils'
var colorScale = [
  'orange',
  'lightblue',
  '#B19CD9',
  'pink',
  'lightgreen',
  'red',
  'yellow',
  'purple',
  'blue',
  'green',
]

const ChartComponent = ({
  colorMode,
  sceneIndex,
  currentScene,
  headerHeight,
}: {
  sceneIndex: number
  colorMode?: 'light' | 'dark'
  currentScene: Scene
  headerHeight?: number
}) => {
  const [simulation, setSimulation] = useState<d3.Simulation<any, undefined>>(
    d3.forceSimulation()
  )
  const [nodes, setNodes] = useState<
    {
      radius: number
      category: string
      color: string
      catIndex: number
      cx?: number
      cy?: number
    }[]
  >(
    currentScene?.categories
      .map((category, catIndex) => {
        return d3.range(category.value).map(function (d, personIndex) {
          return {
            radius: category.radius ?? 5,
            category: category.title as string,
            color: '#fff',
            catIndex: catIndex,
          }
        })
      })
      .flat()
  )
  const maxColLength = 6
  const biggestGroup = Math.max(
    ...currentScene.categories.map((cat) => cat.value)
  )
  const [squareRef, { width, height }] = useElementSize()
  // const [categoriesGroupTextLabelRef, { widthTextLabel, heightTextlabel }] = useElementSize()
  const personsGroupRef = useRef<SVGGElement>(null)
  const categoriesGroupRef = useRef<SVGGElement>(null)
  const categoriesGroupTextLabelRef = useRef<SVGGElement>(null)
  const simulationShouldRun = currentScene.categories.length > 1
  const [changedSceneOnce, setChangedSceneOnce] = useState(false)
  useEffect(() => {
    // currentScene.svgPositions && personsGroupRef.current && setSvgPositions(simulation,nodes, currentScene.svgPositions, width, height, personsGroupRef.current)
    simulation &&
      categoriesGroupTextLabelRef.current &&
      personsGroupRef?.current &&
      width &&
      height &&
      simulationShouldRun &&
      initializeSimulation(
        simulation,
        nodes,
        currentScene,
        width,
        height,
        personsGroupRef.current,
        categoriesGroupTextLabelRef.current
      )
    console.log('🌀 useEffect: [personsGroupRef, simulation, width, height]')
  }, [personsGroupRef, simulation, width, height])
  useLayoutEffect(() => {
    if ((width ?? 0) + (height ?? 0) > 0) {
      const freshNodes = getFreshNodes(
        currentScene,
        width ?? 0,
        height ?? 0,
        !changedSceneOnce
      )
      setChangedSceneOnce(true)

      const nodesUpdated = freshNodes.map((node, index) => {
        const prevNode = nodes?.[index]
        return Object.assign(prevNode || {}, node)
      })

      simulation &&
        personsGroupRef?.current &&
        categoriesGroupTextLabelRef.current &&
        (width ?? 0) > 10 &&
        (height ?? 0) > 10 &&
        // simulationShouldRun &&
        updateSimulation(
          simulation,
          nodes,
          currentScene,
          width ?? 0,
          height ?? 0,
          personsGroupRef.current,
          categoriesGroupTextLabelRef.current
        )
      setNodes(nodesUpdated)
      console.log('🌀 useEffect: [currentScene, height, width]')
    }
  }, [currentScene, height, width])
  useEffect(() => {
    // merge old and new nodes so that the simulation can continue

    const freshNodes = getFreshNodes(
      currentScene,
      width ?? 0,
      height ?? 0,
      !changedSceneOnce
    )
    // console.log('freshNodes', freshNodes);

    const nodesUpdated = freshNodes.map((node, index) => {
      const prevNode = nodes?.[index]
      return Object.assign(prevNode || {}, node)
    })
    // console.log('nodesUpdated', nodesUpdated);

    setNodes(nodesUpdated)
    if (!simulation) {
      console.log('create new simulation')
      setSimulation(d3.forceSimulation())
    }
    console.log('🌀 useEffect: []')
    return () => {
      simulation && simulation.stop()
    }
  }, [])
  return (
    <>
      {currentScene && (
        <ChartWrapper ref={squareRef}>
          <SceneTitle>{currentScene.title}</SceneTitle>
          <SVGChart headerHeight={headerHeight}>
            <filter id='shadow'>
              <feDropShadow
                dx='2'
                dy='2'
                stdDeviation='0'
                floodColor={'rgba(0,0,0,0.3)'}
              />
            </filter>
            <g id='persons' ref={personsGroupRef}>
              <circle cx='37.29198' cy='78.23775' r='5' />
              <circle cx='47.98494' cy='78.23775' r='5' />
              <circle cx='58.63296' cy='78.23775' r='5' />
              <circle cx='69.29405' cy='78.23775' r='5' />
              <circle cx='79.94861' cy='78.25756' r='5' />
              <circle cx='90.59584' cy='78.25756' r='5' />
              <circle cx='101.25773' cy='78.25756' r='5' />
              <circle cx='111.91859' cy='78.23775' r='5' />
              <circle cx='122.55872' cy='78.23775' r='5' />
              <circle cx='133.18781' cy='78.23775' r='5' />
              <circle cx='37.29198' cy='67.78843' r='5' />
              <circle cx='47.98494' cy='67.78843' r='5' />
              <circle cx='58.63296' cy='67.78843' r='5' />
              <circle cx='69.29405' cy='67.78843' r='5' />
              <circle cx='79.94861' cy='67.82805' r='5' />
              <circle cx='90.59584' cy='67.82805' r='5' />
              <circle cx='101.25773' cy='67.82805' r='5' />
              <circle cx='111.91859' cy='67.78843' r='5' />
              <circle cx='122.55872' cy='67.78843' r='5' />
              <circle cx='133.18781' cy='67.78843' r='5' />
              <circle cx='37.29198' cy='57.3391' r='5' />
              <circle cx='47.98494' cy='57.3391' r='5' />
              <circle cx='58.63296' cy='57.3391' r='5' />
              <circle cx='69.29405' cy='57.3391' r='5' />
              <circle cx='79.94861' cy='57.39854' r='5' />
              <circle cx='90.59584' cy='57.39854' r='5' />
              <circle cx='101.25773' cy='57.39854' r='5' />
              <circle cx='111.91859' cy='57.3391' r='5' />
              <circle cx='122.55872' cy='57.3391' r='5' />
              <circle cx='133.18781' cy='57.3391' r='5' />
              <circle cx='37.29198' cy='46.88978' r='5' />
              <circle cx='47.98494' cy='46.88978' r='5' />
              <circle cx='58.63296' cy='46.88978' r='5' />
              <circle cx='69.29405' cy='46.88978' r='5' />
              <circle cx='79.94861' cy='46.96903' r='5' />
              <circle cx='90.59584' cy='46.96903' r='5' />
              <circle cx='101.25773' cy='46.96903' r='5' />
              <circle cx='111.91859' cy='46.88978' r='5' />
              <circle cx='122.55872' cy='46.88978' r='5' />
              <circle cx='133.18781' cy='46.88978' r='5' />
              <circle cx='154.22953' cy='88.68707' r='5' />
              <circle cx='47.98494' cy='36.44046' r='5' />
              <circle cx='58.63296' cy='36.44046' r='5' />
              <circle cx='69.29405' cy='36.44046' r='5' />
              <circle cx='79.94861' cy='36.53952' r='5' />
              <circle cx='90.59584' cy='36.53952' r='5' />
              <circle cx='101.25773' cy='36.53952' r='5' />
              <circle cx='111.91859' cy='36.44046' r='5' />
              <circle cx='122.55872' cy='36.44046' r='5' />
              <circle cx='133.18781' cy='36.44046' r='5' />
              <circle cx='5' cy='88.68707' r='5' />
              <circle cx='47.98494' cy='25.99114' r='5' />
              <circle cx='58.63296' cy='25.99114' r='5' />
              <circle cx='69.29405' cy='25.99114' r='5' />
              <circle cx='79.94861' cy='25.99114' r='5' />
              <circle cx='90.59584' cy='25.99114' r='5' />
              <circle cx='101.25773' cy='25.99114' r='5' />
              <circle cx='111.91859' cy='25.99114' r='5' />
              <circle cx='122.55872' cy='25.99114' r='5' />
              <circle cx='133.18781' cy='25.99114' r='5' />
              <circle cx='143.75298' cy='46.88978' r='5' />
              <circle cx='143.75298' cy='57.3391' r='5' />
              <circle cx='154.22953' cy='78.23775' r='5' />
              <circle cx='69.29405' cy='15.54182' r='5' />
              <circle cx='79.94861' cy='15.54182' r='5' />
              <circle cx='90.59584' cy='15.54182' r='5' />
              <circle cx='101.25773' cy='15.54182' r='5' />
              <circle cx='111.91859' cy='15.54182' r='5' />
              <circle cx='122.55872' cy='15.54182' r='5' />
              <circle cx='101.25773' cy='5' r='5' />
              <circle cx='15.86237' cy='67.78843' r='5' />
              <circle cx='47.98494' cy='88.68707' r='5' />
              <circle cx='58.63296' cy='88.68707' r='5' />
              <circle cx='69.29405' cy='88.68707' r='5' />
              <circle cx='79.94861' cy='88.68707' r='5' />
              <circle cx='90.59584' cy='88.68707' r='5' />
              <circle cx='101.25773' cy='88.68707' r='5' />
              <circle cx='111.91859' cy='88.68707' r='5' />
              <circle cx='122.55872' cy='88.68707' r='5' />
              <circle cx='154.22953' cy='57.3391' r='5' />
              <circle cx='26.51532' cy='46.88978' r='5' />
              <circle cx='47.98494' cy='99.13639' r='5' />
              <circle cx='58.63296' cy='99.13639' r='5' />
              <circle cx='69.29405' cy='99.13639' r='5' />
              <circle cx='79.94861' cy='99.13639' r='5' />
              <circle cx='26.51532' cy='78.23775' r='5' />
              <circle cx='111.91859' cy='109.58571' r='5' />
              <circle cx='111.91859' cy='99.13639' r='5' />
              <circle cx='164.8512' cy='46.88978' r='5' />
              <circle cx='15.86237' cy='88.68707' r='5' />
              <circle cx='26.51532' cy='57.3391' r='5' />
              <circle cx='26.51532' cy='67.78843' r='5' />
              <circle cx='15.86237' cy='78.23775' r='5' />
              <circle cx='143.75298' cy='67.78843' r='5' />
              <circle cx='164.8512' cy='67.78843' r='5' />
              <circle cx='47.98494' cy='109.58571' r='5' />
              <circle cx='37.29198' cy='88.68707' r='5' />
              <circle cx='154.22953' cy='67.78843' r='5' />
              <circle cx='164.8512' cy='57.3391' r='5' />
              <circle cx='143.75298' cy='78.23775' r='5' />
            </g>
            <g id='categories' ref={categoriesGroupRef} fill='transparent'></g>
            <g id='categoriesTextLabel' ref={categoriesGroupTextLabelRef}></g>
          </SVGChart>
        </ChartWrapper>
      )}
    </>
  )
}
const SceneTitle = styled.h2`
  text-align: center;
  color: white;
  font-family: var(--font-sans-serif);
  /* padding-top: .5em; */
  position: absolute;
  font-size: 1.3em;
  width: 100%;
  // place it in the last third of the chart
  left: 50%;
  transform: translateX(-50%);

  @media (min-height: 845px) {
    font-size: 1.5em;
  }

  @media (max-width: 711px) {
    left: 50%;
  }
  img.icon {
    width: 22px;
    height: 30px;
  }
  img.icon.canton {
    width: 20px;
  }
  img.icon.religion {
    height: 26px;
  }
  img.icon.salad {
    width: 24px;
  }
  img.icon.synagogue,
  img.icon.dog,
  img.icon.weights {
    width: 28px;
  }
  img.icon.car,
  img.icon.train {
    width: 32px;
  }
  img.icon.bicycle {
    width: 26px;
  }
  img.icon.house {
    width: 36px;
  }
  img.icon.ballotBox,
  img.icon.necktie,
  img.icon.clock,
  img.icon.house,
  img.icon.beer,
  img.icon.wineGlass,
  img.icon.cocktail,
  img.icon.student,
  img.icon.money {
    margin-top: -6px;
  }
  span {
    display: flex;
    justify-content: center;
    align-items: center;
    span {
      display: block;
      /* align-items: center; */
      img.icon {
        /* margin-bottom: -5px; */
        vertical-align: middle;
      }
      b {
        white-space: nowrap;
      }
    }
  }
  span.secondLine {
    font-weight: 300;
  }

  .smallTitle {
    display: inline-block;
    @media (max-width: 374px) {
      line-height: 1.45em;
      font-size: 0.85em;
    }
    @media (max-width: 340px) {
      line-height: 1.35em;
      font-size: 0.8em;
    }
  }
`
const SVGChart = styled.svg<{ headerHeight?: number }>`
  .grayedOut {
    opacity: 0.5;
  }

  margin-top: 1.5em;
  height: calc(100% - ${({ headerHeight }) => headerHeight ?? 0}px);
  width: 100%;
  overflow: hidden;
  #persons {
    // smooth shadow
    /* filter: url(#shadow); */
    circle {
      box-sizing: border-box;
      stroke: var(--red-main);
      stroke-width: 2px;
      fill: white;
      /* transition: fill 0.1s ease-in-out; */
    }
  }
  #categoriesTextLabel {
    height: 100%;

    .textLabel {
      text-align: center;
      stroke: var(--font-color);
      stroke-width: 3px;
      animation: fadeIn 1s ease-in-out;
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
      }
      font-size: 0.9em;
      @media only screen and (max-width: 711px) {
        font-size: 0.8em;
      }
      @media only screen and (max-height: 799px) {
          font-size: 0.8em;
          .introText {
            font-size: 1em;
          }
      }
      color: var(--font-color);
      font-family: var(--font-sans-serif);
      text-anchor: end;
      width: 100%;
      overflow: visible;
      .labelWrapper {
        width: 160px;
        line-height: 1.4em;
        transform: translate(0, -50%);

        .introText {
          width: 260px;
          transform: translate(-50px, -10px);
          @media only screen and (min-width: 712px) {
            width: 350px;
            font-size: 1.2em;
            line-height: 1.4em;
            transform: translate(-90px, -20px);
          }
        }

        .hint {
          width: 260px;
          transform: translate(-50px, 200px);
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;

          .path {
            width: 16px;
            height: 40px;
            position: absolute;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 40px;
            top: 0px;
            left: 122px;
            @media only screen and (min-width: 1025px) {
              visibility: hidden;
            }
          }

          .hand-icon {
            position: relative;
            background-image: url('https://interaktiv.tagesanzeiger.ch/2023/wenn-die-schweiz-100-einwohner-haette/hand.svg');
            background-repeat: no-repeat;
            background-position: center;
            background-size: 50%;
            opacity: 1;
            width: 60px;
            height: 60px;
            margin-left: 30px;
            transform-origin: 52% 62%;
            animation: swipe-y 1.25s ease-in-out backwards;
            animation-iteration-count: 3;
            @media only screen and (min-width: 1025px) {
              background-image: url('https://interaktiv.tagesanzeiger.ch/2023/wenn-die-schweiz-100-einwohner-haette/mouse.svg');
              background-size: 36%;
              opacity: 0.5;
              margin-left: 0px;
              -webkit-animation: opacity 1.5s ease-in-out backwards;
              -moz-animation: opacity 1.5s ease-in-out backwards;
              -o-animation: opacity 1.5s ease-in-out backwards;
              animation: opacity 1.5s ease-in-out backwards;
              animation-iteration-count: 4;
            }
          }

          @keyframes swipe-y {
            0% {
              transform: translateY(0px);
            }
            25% {
              transform: translateY(12px) rotateZ(-0deg);
            }
            50% {
              transform: translateY(0px);
            }
            75% {
              transform: translateY(-12px) rotateZ(0deg);
            }
          }

          @keyframes opacity {
            0% {
              opacity: 0.3;
            }
            25% {
              opacity: 0.5;
            }
            50% {
              opacity: 0.7;
            }
            75% {
              opacity: 0.9;
            }
          }
        }
        img.icon.swipe {
          width: 40px;
        }
      }

      tspan:first-child {
        font-weight: 500;
      }
      tspan:nth-child(2) {
        fill: var(--orange-main);
        font-weight: 400;
      }
      tspan:first-child {
        font-weight: 500;
      }

      div:nth-child(2) {
        color: var(--orange-main);
        font-weight: 400;
      }
      &.text {
        /* color: var(--site-background); */
        color: #fff;
      }

      &.value {
        z-index: 0;
        font-size: 2.1rem;
        font-weight: 700;
        text-shadow: 2px 2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff,
          -2px -2px 0 #fff, 2px 0px 0 #fff, 0px 2px 0 #fff, -2px 0px 0 #fff,
          0px -2px 0 #fff;
        > div {
          color: var(--brandblue-main);
        }
      }
    }
  }
`
const ChartWrapper = styled.div`
  height: 100%;
`

export default ChartComponent //memo(ChartComponent)
